import { Box, Flex, HStack, IconButton, Text } from "@chakra-ui/react";
import useTranslate from "@raiden/library/hooks/useTranslate";
import { memo } from "react";
import { useIntl } from "react-intl";
import { Icon } from "../../../../../../components/Icon";
import { Z_INDEX } from "../../../../../../constants/zIndex";
import { getMenuItemAdapterValues } from "../../../../../../helpers/menus/getMenuItemAdapterValues";
import { MenuItem } from "./MenuItem";

export const MenuPanel = memo(
  /**
   * @typedef {Object} Props
   * @property {boolean} isOpen
   * @property {() => void} onClose
   * @property {import("@raiden/library/types/Configuration").ConfigurationMenuItem} menuItem
   * @property {() => void} onClickSearch
   * @property {() => void} onClickMenu
   */
  /**
   * @param {Props} props
   */
  function MenuPanel({
    isOpen,
    onClose,
    menuItem,
    onClickSearch,
    onClickMenu,
  }) {
    const intl = useIntl();

    const translate = useTranslate();
    const { title } = getMenuItemAdapterValues({
      menuItem,
      translate,
    });

    return (
      <Flex
        flexDirection="column"
        visibility={isOpen ? "visible" : "hidden"}
        transform={isOpen ? "translateX(0)" : "translateX(100%)"}
        boxShadow="rgba(0, 0, 0, 0.24) 0px 3px 8px"
        transition="all .5s ease-in-out"
        position="fixed"
        top="0"
        left="0"
        right="0"
        bottom="0"
        backgroundColor="brandPrimary.700"
        zIndex={Z_INDEX.HEADER_PANEL}>
        <Box flexShrink={0} py="0.75rem">
          <HStack minH="48px" pl=".25rem" pr="1rem">
            <IconButton
              onClick={onClose}
              icon={<Icon icon="ms_chevron_left" size="2rem" />}
              variant="ghost"
              aria-label={intl.formatMessage({ defaultMessage: "Retour" })}
            />

            <Text
              flexGrow={1}
              fontSize="1.25rem"
              fontWeight={400}
              lineHeight="normal"
              whiteSpace="nowrap"
              overflow="hidden"
              textOverflow="ellipsis">
              {title}
            </Text>

            <HStack spacing="0.25rem">
              <IconButton
                onClick={onClickSearch}
                icon={<Icon icon="ms_search" size="1.5rem" />}
                variant="ghost"
                aria-label={intl.formatMessage({
                  defaultMessage: "Rechercher",
                })}
              />

              <IconButton
                onClick={onClickMenu}
                icon={<Icon icon="ms_close" size="1.5rem" />}
                variant="ghost"
                aria-label={intl.formatMessage({ defaultMessage: "Fermer" })}
              />
            </HStack>
          </HStack>
        </Box>

        <Box px="1rem">
          <Box borderTopWidth="1px" borderColor="gray.400" />
        </Box>

        <Box flexGrow={1} overflow="auto" px="1rem">
          <Box py="1rem">
            {menuItem.children.length > 0 && (
              <>
                {menuItem.children.map((menuItem, index) => (
                  <MenuItem
                    menuItem={menuItem}
                    onClickSearch={onClickSearch}
                    onClickMenu={onClickMenu}
                    key={index}
                  />
                ))}
              </>
            )}
          </Box>
        </Box>
      </Flex>
    );
  },
);
